import { API_URL, FUNCTIONS_API_URL } from '@/config/settings';
import { cache } from '@/store';

const getFunctionsApiUri = uri => getUri(FUNCTIONS_API_URL, `/api/${uri}`);

const getApiUri = uri => getUri(API_URL, uri);

const getUri = (endpoint = null, uri = null) => {
  const formattedUri = `/${uri}`.replace(/\/+/g, '/');
  return `${endpoint}${formattedUri}`;
};

const imageExists = url => {
  return new Promise(resolve => {
    let img = new Image();
    img.addEventListener('load', () => resolve(true));
    img.addEventListener('error', () => resolve(false));
    img.src = url;
  });
};

const videoExists = async videoUrl => {
  return await cache.getOrAddAsync({
    key: `video-exists-${videoUrl}`,
    callback: async () => {
      try {
        const response = await fetch(videoUrl, { method: 'HEAD' });
        return response.ok;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    ttlMinutes: 60 * 60 * 24,
    isUseSessionStorage: true,
    typename: 'videoExists'
  });
};

/**
 * This func handles common api errors that can be safely ignored
 * If an error can not be ignored, it will throw the same error
 * You can use 'isApiErrorIgnorable' for a simpler if/else check
 * @throws Will re-throw the provided error if it already exists
 */
const catchCommonErrors = e => {
  if (isApiErrorIgnorable(e)) return;

  throw new Error(e);
};

/**
 * This func handles common api errors that can be safely ignored
 * @return {boolean} - Returns true if the error can be ignored, otherwise returns false
 */
const isApiErrorIgnorable = e => {
  if (e?.toString()) {
    let error = e.toString();

    // Note: safari believes 204/no content is a bad thing
    // So just mute this as this has been spamming sentry
    if (error.includes('Load failed')) {
      return true;
    }

    // Note: chrome(ium) based browsers may return this
    // when fetch has been canceled (e.g. page swap), or prevented (e.g. ad blocker)
    if (error.includes('Failed to fetch')) {
      return true;
    }
  }

  return false;
};

const captionExists = async vttUrl => {
  return await cache.getOrAddAsync({
    key: `caption-exists-${vttUrl}`,
    callback: async () => {
      try {
        const fetchResponse = await fetch(vttUrl, { method: 'HEAD' });

        if (fetchResponse.ok) {
          const response = await fetch(vttUrl);
          return {
            isCaptionExist: response.ok,
            caption: response.ok ? await response.text() : ''
          };
        } else {
          return { isCaptionExist: false };
        }
      } catch (e) {
        console.error(e);
        return { isCaptionExist: false };
      }
    },
    ttlMinutes: 60 * 60 * 24,
    isUseSessionStorage: true,
    typename: 'captionExists'
  });
};

export {
  getApiUri,
  getFunctionsApiUri,
  getUri,
  imageExists,
  videoExists,
  captionExists,
  catchCommonErrors,
  isApiErrorIgnorable
};
